import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState, type ReactNode } from 'react'
import { cascadeOptionsAt } from '@shared/properties'
import { replyTargetLabel } from '@shared/duration'
import { api, ApiError, mediaUrl, type InboxConversation, type InboxTicket, type PropertyView } from '../lib/api'
import { PriorityIcon, TicketStatusDot } from './TicketBadges'
import { TaskForm } from './Tasks'

/**
 * The side panel's content, switched by the tab the inbox is showing.
 *
 * Details and Ticket are the sector's own fields — the industry blueprint made concrete at
 * the moment an agent actually fills them in. Contact is the identity of whoever is on the
 * other end, which is also where masking becomes visible.
 */
export function PropertiesPanel({
  conversationId,
  ticketId,
  view,
  conversation,
  ticket,
  masked,
}: {
  conversationId: string
  ticketId?: string
  view: 'properties' | 'ticket' | 'contact' | 'tasks'
  conversation: InboxConversation
  ticket?: InboxTicket
  masked: boolean
}) {
  const queryClient = useQueryClient()

  const chat = useQuery({
    queryKey: ['chat-properties', conversationId],
    queryFn: () => api.chatProperties(conversationId),
    retry: false,
  })

  const ticketProps = useQuery({
    queryKey: ['ticket-properties', ticketId],
    queryFn: () => api.ticketProperties(ticketId!),
    enabled: Boolean(ticketId),
    retry: false,
  })

  const saveChat = useMutation({
    mutationFn: (input: { defId: string; value: string | null }) =>
      api.setChatProperty(conversationId, input.defId, input.value),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['chat-properties', conversationId] }),
  })

  const saveTicket = useMutation({
    mutationFn: (input: { defId: string; value: string | null }) =>
      api.setTicketProperty(ticketId!, input.defId, input.value),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['ticket-properties', ticketId] }),
  })

  const close = useMutation({
    mutationFn: () => api.closeTicket(ticketId!),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['ticket-properties', ticketId] }),
        queryClient.invalidateQueries({ queryKey: ['inbox'] }),
        queryClient.invalidateQueries({ queryKey: ['conversation', conversationId] }),
      ])
    },
  })

  const [editingTicket, setEditingTicket] = useState(false)
  const [confirmingDeleteTicket, setConfirmingDeleteTicket] = useState(false)
  const [ticketTitle, setTicketTitle] = useState('')
  const [ticketCategory, setTicketCategory] = useState('')
  const [ticketPriority, setTicketPriority] = useState<'low' | 'normal' | 'high' | 'urgent'>('normal')
  const [ticketAssignee, setTicketAssignee] = useState('')

  const openTicketEdit = () => {
    if (!ticket) return
    setTicketTitle(ticket.title)
    setTicketCategory(ticket.category)
    setTicketPriority(ticket.priority)
    setTicketAssignee(ticket.assigneeUserId ?? '')
    setEditingTicket(true)
  }

  const updateTicket = useMutation({
    mutationFn: () =>
      api.updateTicket(ticketId!, {
        title: ticketTitle.trim(),
        category: ticketCategory.trim(),
        priority: ticketPriority,
        assigneeUserId: ticketAssignee || null,
      }),
    onSuccess: async () => {
      setEditingTicket(false)
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['inbox'] }),
        queryClient.invalidateQueries({ queryKey: ['conversation', conversationId] }),
      ])
    },
  })

  const deleteTicket = useMutation({
    mutationFn: () => api.deleteTicket(ticketId!),
    onSuccess: async () => {
      setConfirmingDeleteTicket(false)
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['inbox'] }),
        queryClient.invalidateQueries({ queryKey: ['conversation', conversationId] }),
      ])
    },
  })

  const [addingTask, setAddingTask] = useState(false)
  const conversationTasks = useQuery({
    queryKey: ['tasks', 'conversation', conversationId],
    queryFn: () => api.tasks({ conversationId }),
    enabled: view === 'tasks',
    retry: false,
  })
  const team = useQuery({
    queryKey: ['team'],
    queryFn: api.team,
    enabled: view === 'tasks' || view === 'properties' || view === 'ticket',
    retry: false,
  })
  const refreshTasks = () =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: ['tasks', 'conversation', conversationId] }),
      queryClient.invalidateQueries({ queryKey: ['tasks', 'open', ''] }),
    ])
  const completeTask = useMutation({ mutationFn: (id: string) => api.completeTask(id), onSuccess: refreshTasks })
  const reopenTask = useMutation({ mutationFn: (id: string) => api.reopenTask(id), onSuccess: refreshTasks })

  const conversationLabels = useQuery({
    queryKey: ['conversation-labels', conversationId],
    queryFn: () => api.conversationLabels(conversationId),
    enabled: view === 'properties',
    retry: false,
  })
  const labelAction = useMutation({
    mutationFn: (input: { action: 'add_label' | 'remove_label'; labelId: string }) =>
      api.bulkInboxAction({ action: input.action, conversationIds: [conversationId], labelId: input.labelId }),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['conversation-labels', conversationId] }),
        // The thread-list row's own label chips come from the `inbox` list query, not this
        // panel — without this, adding a label here would look like it did nothing until the
        // next unrelated refetch.
        queryClient.invalidateQueries({ queryKey: ['inbox'] }),
      ])
    },
  })

  if (view === 'contact') {
    return (
      <dl className="cad-kv">
        <Row label="Chat" value={conversation.subject ?? conversation.remoteId} />
        <Row label="Kind" value={conversation.kind === 'group' ? 'Group' : 'Direct message'} />
        <Row label="On number" value={conversation.channelNumber} />
        <Row label="Unread" value={String(conversation.unreadCount)} />
        {conversation.contactOptedOut !== null && conversation.contactOptedOut !== undefined && (
          <Row label="Marketing opt-in" value={conversation.contactOptedOut ? 'No — asked to stop' : 'Yes'} />
        )}
        {masked && (
          <p className="cad-note" style={{ gridColumn: '1 / -1' }}>
            Phone numbers are hidden for your role. The reference stays the same for the same
            person, so you can still tell two contacts apart.
          </p>
        )}
      </dl>
    )
  }

  if (view === 'tasks') {
    if (addingTask) {
      return (
        <TaskForm
          conversationId={conversationId}
          ticketId={ticketId}
          members={team.data?.members ?? []}
          onDone={() => {
            setAddingTask(false)
            void refreshTasks()
          }}
          onCancel={() => setAddingTask(false)}
        />
      )
    }

    const items = conversationTasks.data?.tasks ?? []
    return (
      <div className="stack gap-3">
        <button type="button" className="cad-btn" data-variant="quiet" onClick={() => setAddingTask(true)}>
          New task
        </button>
        {conversationTasks.isLoading ? (
          <div style={{ height: '80px' }} />
        ) : items.length === 0 ? (
          <p className="cad-muted">No tasks linked to this chat yet.</p>
        ) : (
          <div className="stack gap-2">
            {items.map((task) => (
              <div key={task.id} className="cad-card" style={{ padding: '10px 12px' }}>
                <div className="row between gap-2" style={{ alignItems: 'flex-start' }}>
                  <div className="stack gap-1">
                    <span style={{ fontWeight: 500 }}>{task.title}</span>
                    {task.dueAt && (
                      <span className="cad-muted" style={{ fontSize: 'var(--cad-text-base)' }}>
                        {task.overdue && (
                          <span className="cad-pill" data-tone="bad" style={{ marginRight: '6px' }}>
                            overdue
                          </span>
                        )}
                        Due {new Date(task.dueAt).toLocaleString(undefined, { dateStyle: 'short', timeStyle: 'short' })}
                      </span>
                    )}
                  </div>
                  {task.status === 'open' ? (
                    <button
                      type="button"
                      className="cad-btn"
                      data-variant="text"
                      onClick={() => completeTask.mutate(task.id)}
                    >
                      Complete
                    </button>
                  ) : (
                    <button
                      type="button"
                      className="cad-btn"
                      data-variant="text"
                      onClick={() => reopenTask.mutate(task.id)}
                    >
                      Reopen
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    )
  }

  if (view === 'ticket') {
    if (!ticket || !ticketId) {
      return <p className="cad-muted">No open ticket on this conversation.</p>
    }


    const properties = ticketProps.data?.properties ?? []
    const outstanding = properties.filter((p) => p.requiredBeforeClose && !p.value)
    const closeError = close.error
    const closeMessage =
      closeError instanceof ApiError || closeError instanceof Error ? closeError.message : undefined
    const updateError = updateTicket.error
    const updateMessage =
      updateError instanceof ApiError || updateError instanceof Error ? updateError.message : undefined
    const deleteError = deleteTicket.error
    const deleteMessage =
      deleteError instanceof ApiError || deleteError instanceof Error ? deleteError.message : undefined
    const assigneeName = team.data?.members.find((m) => m.userId === ticket.assigneeUserId)?.name ?? 'Unassigned'

    return (
      <div className="stack gap-3">
        {editingTicket ? (
          <div className="stack gap-2">
            <div className="cad-field">
              <label htmlFor="ticket-title">Title</label>
              <input
                id="ticket-title"
                className="cad-input"
                value={ticketTitle}
                onChange={(e) => setTicketTitle(e.target.value)}
              />
            </div>
            <div className="cad-field">
              <label htmlFor="ticket-category">Category</label>
              <input
                id="ticket-category"
                className="cad-input"
                value={ticketCategory}
                onChange={(e) => setTicketCategory(e.target.value)}
              />
            </div>
            <div className="cad-field">
              <label htmlFor="ticket-priority">Priority</label>
              <select
                id="ticket-priority"
                className="cad-input"
                value={ticketPriority}
                onChange={(e) => setTicketPriority(e.target.value as typeof ticketPriority)}
              >
                <option value="low">Low</option>
                <option value="normal">Normal</option>
                <option value="high">High</option>
                <option value="urgent">Urgent</option>
              </select>
            </div>
            <div className="cad-field">
              <label htmlFor="ticket-assignee">Assigned to</label>
              <select
                id="ticket-assignee"
                className="cad-input"
                value={ticketAssignee}
                onChange={(e) => setTicketAssignee(e.target.value)}
              >
                <option value="">Unassigned</option>
                {(team.data?.members ?? []).map((member) => (
                  <option key={member.userId} value={member.userId}>
                    {member.name}
                  </option>
                ))}
              </select>
            </div>
            {updateMessage && (
              <p className="cad-note" style={{ borderColor: 'var(--cad-bad)' }}>{updateMessage}</p>
            )}
            <div className="row gap-2 wrap">
              <button
                type="button"
                className="cad-btn"
                disabled={!ticketTitle.trim() || updateTicket.isPending}
                onClick={() => updateTicket.mutate()}
              >
                {updateTicket.isPending ? 'Saving…' : 'Save changes'}
              </button>
              <button type="button" className="cad-btn" data-variant="text" onClick={() => setEditingTicket(false)}>
                Cancel
              </button>
            </div>
          </div>
        ) : (
          <>
            <dl className="cad-kv">
              <Row label="Reference" value={ticket.reference} />
              <Row label="Title" value={ticket.title} />
              {/* Status and the reply target used to live only as pills in the thread header.
                  They moved here with the rest of the ticket metadata, so this tab is now the
                  one place that describes the ticket rather than half of it. */}
              {/* Rendered through the shared components rather than as raw strings: a
                  workspace can rename its stages (Pending -> "In progress"), so printing
                  ticket.status directly would quietly ignore that setting. */}
              <Row label="Status" node={<TicketStatusDot status={ticket.status} />} />
              <Row label="Category" value={ticket.category} />
              <Row
                label="Priority"
                node={
                  <span className="row gap-1" style={{ alignItems: 'center' }}>
                    <PriorityIcon priority={ticket.priority} />
                    {ticket.priority}
                  </span>
                }
              />
              <Row label="Reply target" value={replyTargetLabel(ticket)} />
              <Row label="Assigned to" value={assigneeName} />
              <Row label="Opened by" value={ticket.matchedRule ?? 'An agent'} />
            </dl>
            <div className="row gap-2 wrap">
              <button type="button" className="cad-btn" data-variant="text" onClick={openTicketEdit}>
                Edit ticket
              </button>
              {team.data?.canManage &&
                (confirmingDeleteTicket ? (
                  <>
                    <span className="cad-muted" style={{ fontSize: 'var(--cad-text-base)' }}>
                      Delete this ticket? The chat and its messages stay; the ticket, its status and
                      its reply target do not.
                    </span>
                    <button
                      type="button"
                      className="cad-btn"
                      data-variant="text"
                      disabled={deleteTicket.isPending}
                      onClick={() => deleteTicket.mutate()}
                    >
                      {deleteTicket.isPending ? 'Deleting…' : 'Confirm delete'}
                    </button>
                    <button
                      type="button"
                      className="cad-btn"
                      data-variant="text"
                      onClick={() => setConfirmingDeleteTicket(false)}
                    >
                      Cancel
                    </button>
                  </>
                ) : (
                  <button
                    type="button"
                    className="cad-btn"
                    data-variant="text"
                    onClick={() => setConfirmingDeleteTicket(true)}
                  >
                    Delete ticket
                  </button>
                ))}
            </div>
            {deleteMessage && (
              <p className="cad-note" style={{ borderColor: 'var(--cad-bad)' }}>{deleteMessage}</p>
            )}
          </>
        )}

        <Fields
          properties={properties}
          onSave={(defId, value) => saveTicket.mutate({ defId, value })}
        />

        {closeMessage && (
          <p className="cad-note" style={{ borderColor: 'var(--cad-bad)' }}>{closeMessage}</p>
        )}

        <button
          type="button"
          className="cad-btn"
          disabled={close.isPending}
          onClick={() => close.mutate()}
        >
          {close.isPending ? 'Closing…' : 'Close ticket'}
        </button>

        {outstanding.length > 0 && (
          <p className="cad-note">
            {outstanding.map((p) => p.name).join(' and ')} must be recorded first — this is the
            record your industry needs when someone asks later.
          </p>
        )}
      </div>
    )
  }

  if (chat.isError) return <p className="cad-muted">Fields unavailable.</p>

  return (
    <div className="stack gap-3">
      {/* Who the chat itself is assigned to.
          These were pills in the thread header, removed when ticket metadata moved into this
          panel — but the Ticket tab shows `ticket.assigneeUserId`, which is a different field,
          and shows nothing at all on a conversation with no open ticket. The assigned *team*
          had no remaining home anywhere in the product. Conversation-level assignment belongs
          here, beside the conversation's other properties, not on the ticket. */}
      {(conversation.assignedTo || conversation.assignedTeamId) && (
        <dl className="cad-kv">
          {conversation.assignedTeamId && (
            <Row
              label="Assigned team"
              value={team.data?.teams.find((t) => t.id === conversation.assignedTeamId)?.name ?? 'A team'}
            />
          )}
          {conversation.assignedTo && (
            <Row
              label="Assigned to"
              value={team.data?.members.find((m) => m.userId === conversation.assignedTo)?.name ?? 'A teammate'}
            />
          )}
        </dl>
      )}
      <AiResponderControl conversationId={conversationId} state={conversation.aiResponderState ?? null} />
      <LabelPicker
        applied={conversationLabels.data?.labels ?? []}
        available={team.data?.labels ?? []}
        loading={conversationLabels.isLoading}
        error={labelAction.error}
        pending={labelAction.isPending}
        onAdd={(labelId) => labelAction.mutate({ action: 'add_label', labelId })}
        onRemove={(labelId) => labelAction.mutate({ action: 'remove_label', labelId })}
      />
      <Fields
        properties={chat.data?.properties ?? []}
        onSave={(defId, value) => saveChat.mutate({ defId, value })}
      />
    </div>
  )
}

const AI_STATE_LABEL: Record<'active' | 'thinking' | 'snoozed' | 'inactive', string> = {
  active: 'Active',
  thinking: 'Thinking…',
  snoozed: 'Snoozed',
  inactive: 'Off for this chat',
}

/**
 * A person's own override of the workspace's AI-auto-reply setting, for this one chat. Shown
 * even when the workspace setting is off — it's a harmless no-op override until it's turned
 * on, and a person switching between chats sees the same control everywhere rather than one
 * that appears and disappears.
 *
 * 'thinking' and 'snoozed' have no button of their own: the system moves into and out of
 * both on its own (see `runAutoReply` and `recordOutboundMessage`), so the only actions worth
 * offering here are turning this one chat off, or turning it — or handing it back — on.
 */
function AiResponderControl({
  conversationId,
  state,
}: {
  conversationId: string
  state: 'active' | 'thinking' | 'snoozed' | 'inactive' | null
}) {
  const queryClient = useQueryClient()
  const setState = useMutation({
    mutationFn: (next: 'active' | 'inactive') => api.setAiResponderState(conversationId, next),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['conversation', conversationId] }),
  })

  const effective = state ?? 'active'
  // Both 'inactive' (a deliberate per-chat opt-out) and 'snoozed' (the system paused it after a
  // person replied) resume the same way — the button hands it back to 'active' either way.
  const paused = effective === 'inactive' || effective === 'snoozed'

  return (
    <div className="row gap-2 wrap" style={{ alignItems: 'center' }}>
      <span className="cad-eyebrow">AI replies</span>
      <span className="cad-pill" data-tone={effective === 'active' ? 'good' : effective === 'snoozed' ? 'warn' : undefined}>
        {AI_STATE_LABEL[effective]}
      </span>
      <button
        type="button"
        className="cad-btn"
        data-variant="text"
        disabled={setState.isPending}
        onClick={() => setState.mutate(paused ? 'active' : 'inactive')}
      >
        {effective === 'snoozed' ? 'Reactivate' : paused ? 'Turn on for this chat' : 'Turn off for this chat'}
      </button>
    </div>
  )
}

/**
 * Labels on the chat that's actually open — previously the only way to label a conversation
 * was to leave it, select it (and possibly others) from the list view, and use the bulk-action
 * bar's label picker. One click here does the same `add_label`/`remove_label` call the bulk bar
 * already makes, just scoped to this one chat.
 */
function LabelPicker({
  applied,
  available,
  loading,
  error,
  pending,
  onAdd,
  onRemove,
}: {
  applied: { id: string; name: string; color: string }[]
  available: { id: string; name: string; color: string }[]
  loading: boolean
  error: unknown
  pending: boolean
  onAdd: (labelId: string) => void
  onRemove: (labelId: string) => void
}) {
  const [picking, setPicking] = useState(false)
  const appliedIds = new Set(applied.map((l) => l.id))
  const addable = available.filter((l) => !appliedIds.has(l.id))
  const message = error instanceof ApiError || error instanceof Error ? error.message : undefined

  return (
    <div className="stack gap-2">
      <span className="cad-eyebrow">Labels</span>
      {loading ? (
        <div style={{ height: '24px' }} />
      ) : (
        <div className="row gap-2 wrap" style={{ alignItems: 'center' }}>
          {applied.map((label) => (
            <span key={label.id} className="cad-pill" style={{ borderColor: label.color }}>
              {label.name}
              <button
                type="button"
                aria-label={`Remove ${label.name}`}
                disabled={pending}
                onClick={() => onRemove(label.id)}
                style={{ marginLeft: '6px', border: 'none', background: 'none', cursor: 'pointer', padding: 0 }}
              >
                ×
              </button>
            </span>
          ))}
          {picking ? (
            addable.length === 0 ? (
              <span className="cad-muted" style={{ fontSize: 'var(--cad-text-base)' }}>Every label is already on this chat.</span>
            ) : (
              <select
                className="cad-input"
                style={{ width: 'auto' }}
                autoFocus
                value=""
                disabled={pending}
                onChange={(e) => {
                  if (e.target.value) onAdd(e.target.value)
                  setPicking(false)
                }}
                onBlur={() => setPicking(false)}
              >
                <option value="" disabled>
                  Choose a label…
                </option>
                {addable.map((l) => (
                  <option key={l.id} value={l.id}>
                    {l.name}
                  </option>
                ))}
              </select>
            )
          ) : (
            <button type="button" className="cad-btn" data-variant="text" onClick={() => setPicking(true)}>
              + Add label
            </button>
          )}
        </div>
      )}
      {message && (
        <p className="cad-note" style={{ borderColor: 'var(--cad-bad)' }}>
          {message}
        </p>
      )}
    </div>
  )
}

/** `value` for the ordinary string case; `node` when the value carries its own rendering —
 *  a status whose label and colour are workspace-configured, say, rather than a raw enum. */
function Row({ label, value, node }: { label: string; value?: string; node?: ReactNode }) {
  return (
    <>
      <dt>{label}</dt>
      <dd>{node ?? value}</dd>
    </>
  )
}

function Fields({
  properties,
  onSave,
}: {
  properties: PropertyView[]
  onSave: (defId: string, value: string | null) => void
}) {
  if (properties.length === 0) {
    return <p className="cad-muted">No fields configured. Add them from Settings.</p>
  }

  // Grouped by the section the blueprint set, so a clinic reads Patient and Compliance
  // rather than a flat list of everything at once.
  const sections = properties.reduce<Record<string, PropertyView[]>>((acc, property) => {
    ;(acc[property.section] ??= []).push(property)
    return acc
  }, {})

  return (
    <div className="stack gap-3">
      {Object.entries(sections).map(([section, fields]) => (
        <div key={section} className="stack gap-2">
          <span className="cad-eyebrow">{section}</span>
          {fields.map((property) => (
            <Field
              key={property.id}
              property={property}
              onSave={(value) => onSave(property.id, value)}
            />
          ))}
        </div>
      ))}
    </div>
  )
}

function Field({
  property,
  onSave,
}: {
  property: PropertyView
  onSave: (value: string | null) => void
}) {
  const [draft, setDraft] = useState(property.value ?? '')
  const fieldId = `prop-${property.id}`
  const missing = property.requiredBeforeClose && !property.value

  // Committed on blur rather than per keystroke: typing a reference should not produce a
  // write per character.
  const commit = () => {
    if (draft !== (property.value ?? '')) onSave(draft || null)
  }

  return (
    <div className="cad-field">
      <label htmlFor={fieldId}>
        {property.name}
        {property.requiredBeforeClose && (
          <span style={{ color: 'var(--cad-bad)' }}> · required</span>
        )}
      </label>

      {property.type === 'single_select' ? (
        <select
          id={fieldId}
          className="cad-input"
          value={draft}
          data-missing={missing}
          onChange={(event) => {
            setDraft(event.target.value)
            onSave(event.target.value || null)
          }}
        >
          <option value="">Not set</option>
          {property.options.map((option) => (
            <option key={option} value={option}>
              {option}
            </option>
          ))}
        </select>
      ) : property.type === 'multi_select' ? (
        <div className="row gap-2 wrap">
          {property.options.map((option) => {
            const selected = draft.split(',').filter(Boolean)
            const on = selected.includes(option)
            return (
              <button
                key={option}
                type="button"
                className="cad-pill"
                data-tone={on ? 'accent' : undefined}
                aria-pressed={on}
                onClick={() => {
                  const next = on ? selected.filter((s) => s !== option) : [...selected, option]
                  setDraft(next.join(','))
                  onSave(next.join(',') || null)
                }}
              >
                {option}
              </button>
            )
          })}
        </div>
      ) : property.type === 'file' ? (
        <FileField value={property.value} onSave={onSave} />
      ) : property.type === 'cascading_select' ? (
        <CascadingField
          paths={property.cascadeOptions}
          value={draft}
          onChange={(next) => {
            setDraft(next)
            onSave(next || null)
          }}
        />
      ) : (
        <input
          id={fieldId}
          className="cad-input"
          type={property.type === 'date' ? 'date' : property.type === 'number' ? 'number' : 'text'}
          value={draft}
          data-missing={missing}
          onChange={(event) => setDraft(event.target.value)}
          onBlur={commit}
        />
      )}

      {property.hint && (
        <span className="cad-muted" style={{ fontSize: 'var(--cad-text-base)' }}>
          {property.hint}
        </span>
      )}
    </div>
  )
}

/** Human-readable size, matching the granularity a person actually cares about — "48 KB" is
 *  useful, "48000 bytes" is not. */
function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

interface StoredFile {
  key: string
  fileName: string
  mime: string
  size: number
}

/**
 * A file-type property, backed by the same R2 upload pipeline every other attachment in the
 * product already goes through. Previously this type was selectable in Settings but rendered
 * as inert text everywhere it was actually used — an admin could offer the field, but no agent
 * could ever fill it in. The value is stored as a small JSON blob (key/name/mime/size) in the
 * same text column every other property type uses, the same way a cascading-select path is.
 */
function FileField({ value, onSave }: { value: string | null; onSave: (value: string | null) => void }) {
  const upload = useMutation({
    mutationFn: (file: File) => api.uploadMedia(file),
    onSuccess: (media) =>
      onSave(JSON.stringify({ key: media.key, fileName: media.fileName, mime: media.mime, size: media.size })),
  })

  let stored: StoredFile | null = null
  if (value) {
    try {
      stored = JSON.parse(value) as StoredFile
    } catch {
      stored = null
    }
  }

  if (upload.isPending) {
    return <span className="cad-muted" style={{ fontSize: 'var(--cad-text-base)' }}>Uploading…</span>
  }

  if (stored) {
    return (
      <div className="stack gap-1">
        <div className="row gap-2 wrap" style={{ alignItems: 'center' }}>
          <a href={mediaUrl(stored.key)} target="_blank" rel="noreferrer" style={{ fontSize: 'var(--cad-text-base)' }}>
            {stored.fileName}
          </a>
          <span className="cad-muted" style={{ fontSize: 'var(--cad-text-sm)' }}>{formatBytes(stored.size)}</span>
          <button type="button" className="cad-btn" data-variant="text" onClick={() => onSave(null)}>
            Remove
          </button>
        </div>
        {upload.isError && (
          <p className="cad-note" style={{ borderColor: 'var(--cad-bad)' }}>
            {upload.error instanceof ApiError || upload.error instanceof Error
              ? upload.error.message
              : 'Could not upload that file.'}
          </p>
        )}
      </div>
    )
  }

  return (
    <div className="stack gap-1">
      <input
        type="file"
        onChange={(event) => {
          const file = event.target.files?.[0]
          if (file) upload.mutate(file)
          event.target.value = ''
        }}
      />
      {upload.isError && (
        <p className="cad-note" style={{ borderColor: 'var(--cad-bad)' }}>
          {upload.error instanceof ApiError || upload.error instanceof Error
            ? upload.error.message
            : 'Could not upload that file.'}
        </p>
      )}
    </div>
  )
}

/** One `<select>` per level, each populated from only the paths the level above chose —
 *  a dependent select up to three levels deep, built at render time from the flat path list
 *  rather than a stored tree, since grouping by prefix is all a chain of selects needs. */
function CascadingField({
  paths,
  value,
  onChange,
}: {
  paths: string[][]
  value: string
  onChange: (next: string) => void
}) {
  const chosen = value ? value.split(' > ') : []
  const maxDepth = Math.max(0, ...paths.map((p) => p.length))
  const selects: { depth: number; options: string[] }[] = []
  for (let depth = 0; depth < maxDepth; depth++) {
    const options = cascadeOptionsAt(paths, chosen.slice(0, depth))
    if (options.length === 0) break
    selects.push({ depth, options })
    // The next level only exists once this one actually has a choice — otherwise every
    // level before the first choice would show the same top-level options repeated, since
    // `chosen.slice(0, depth)` is `[]` at every depth until something has been picked.
    if (!chosen[depth]) break
  }

  return (
    <div className="row gap-2 wrap">
      {selects.map(({ depth, options }) => (
        <select
          key={depth}
          className="cad-input"
          value={chosen[depth] ?? ''}
          onChange={(event) => {
            const next = [...chosen.slice(0, depth), event.target.value].filter(Boolean)
            onChange(next.join(' > '))
          }}
        >
          <option value="">Choose…</option>
          {options.map((option) => (
            <option key={option} value={option}>
              {option}
            </option>
          ))}
        </select>
      ))}
    </div>
  )
}
